import type { IAssistantExcludedRow } from '@/interfaces/assistant.interface';
import type { IGhCalloutCard, IGhCalloutRow } from '../gpthelp-host-contract';
import { clampText } from './card-text';
import type { ICardFormat } from './card-text';
import { isCategoryExclusion, rowItemLabel } from './row-values';

/** Host contract §3.3 caps of a callout. */
const TITLE_MAX = 160;
const TEXT_MAX = 600;
const ROW_LABEL_MAX = 160;
const ROW_VALUE_MAX = 60;
/** Products the card names; the rest are counted in the last row (≤8 rows). */
const NAMED_MAX = 7;

/** The reasons this release words; a newer one reads «No se puede cambiar así». */
const KNOWN_REASONS: ReadonlyArray<IAssistantExcludedRow['reason']> = [
  'NO_CHANGE',
  'DISCOUNT_INVALID',
  'PRICE_INVALID',
  'NO_BASE_PRICE',
  'HIDDEN_EVERYWHERE',
];

/** The text key of why a row was left out. */
function reasonKey(row: IAssistantExcludedRow): string {
  if (!KNOWN_REASONS.includes(row.reason))
    return 'assistant.empty.reason.other';
  if (row.reason === 'HIDDEN_EVERYWHERE' && isCategoryExclusion(row))
    return 'assistant.empty.reason.HIDDEN_EVERYWHERE_category';
  return `assistant.empty.reason.${row.reason}`;
}

/** «Ya está así», «No tiene precio», «Oculta en todas las sucursales»… */
function reasonText(row: IAssistantExcludedRow, fmt: ICardFormat): string {
  return clampText(fmt.t(reasonKey(row)), ROW_VALUE_MAX);
}

/**
 * Whom to ask when only a member over every branch can show what was left
 * out: of a category when every such row is a category (plan P2-4), else of
 * the products; null when no row was left out for it.
 */
function hiddenEverywhereHintKey(
  excluded: IAssistantExcludedRow[]
): string | null {
  const hidden = excluded.filter((row) => row.reason === 'HIDDEN_EVERYWHERE');
  if (!hidden.length) return null;
  return hidden.every(isCategoryExclusion)
    ? 'assistant.empty.hint.HIDDEN_EVERYWHERE_category'
    : 'assistant.empty.hint.HIDDEN_EVERYWHERE';
}

/**
 * «No hay nada para aplicar» (plan P0-6): a proposal that prepared nothing,
 * with each product or category left out and why (the first 7, then «y {n}
 * más»), and whom to ask when only a member over every branch can show it.
 */
export function nothingToChangeCard(
  id: string,
  excluded: IAssistantExcludedRow[],
  fmt: ICardFormat
): IGhCalloutCard {
  const card: IGhCalloutCard = {
    id,
    v: 1,
    type: 'callout',
    tone: 'info',
    icon: 'info',
    title: clampText(fmt.t('assistant.empty.title'), TITLE_MAX),
  };
  const rows: IGhCalloutRow[] = excluded.slice(0, NAMED_MAX).map((row) => ({
    label: clampText(rowItemLabel(row), ROW_LABEL_MAX),
    value: reasonText(row, fmt),
  }));
  const more = excluded.length - NAMED_MAX;
  if (more > 0)
    rows.push({
      label: clampText(
        fmt.t('assistant.activity.more', { n: fmt.number(more) }),
        ROW_LABEL_MAX
      ),
    });
  if (rows.length) card.rows = rows;
  const hint = hiddenEverywhereHintKey(excluded);
  if (hint) card.text = clampText(fmt.t(hint), TEXT_MAX);
  return card;
}
